import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Calendar, Clock, 
  CheckCircle, XCircle, AlertCircle, LogOut,
  ChevronRight, Scissors, Eye
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  pending_payment: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Awaiting Payment' },
  payment_uploaded: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Payment Sent' },
  confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Confirmed' },
  completed: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Completed' },
  cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' },
  no_show: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'No Show' },
};

export const Account = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const storedCustomer = localStorage.getItem('customer');
    if (storedCustomer) {
      const data = JSON.parse(storedCustomer);
      setCustomer(data);
      setIsLoggedIn(true);
      fetchBookings(data.id);
    }
  }, []);

  const fetchBookings = async (customerId) => {
    try {
      const res = await axios.get(`${API}/customers/${customerId}/bookings`);
      setBookings(res.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/customers/register`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });

      localStorage.setItem('customer', JSON.stringify(res.data.customer));
      localStorage.setItem('customer_token', res.data.token);
      setCustomer(res.data.customer);
      setIsLoggedIn(true);
      toast.success('Account created! Welcome to BraidsByMel!');
      fetchBookings(res.data.customer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const res = await axios.post(`${API}/customers/login`, {
        email: formData.email,
        password: formData.password
      });

      localStorage.setItem('customer', JSON.stringify(res.data.customer));
      localStorage.setItem('customer_token', res.data.token);
      setCustomer(res.data.customer);
      setIsLoggedIn(true);
      toast.success(`Welcome back, ${res.data.customer.name}!`);
      fetchBookings(res.data.customer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer');
    localStorage.removeItem('customer_token');
    setCustomer(null);
    setIsLoggedIn(false);
    setBookings([]);
    setFormData({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  };

  // Login/Register Form
  if (!isLoggedIn) {
    return (
      <div className="page-transition pt-24 min-h-screen">
        <div className="max-w-md mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-surface border border-gold/30 rounded-2xl p-8"
          >
            <h1 className="font-script text-4xl text-gold text-center mb-2">
              {isRegister ? 'Join BraidsByMel' : 'Welcome Back'}
            </h1>
            <p className="text-[#A3A3A3] text-center mb-8">
              {isRegister ? 'Create your account to book appointments' : 'Sign in to view your bookings'}
            </p>

            <form onSubmit={isRegister ? handleRegister : handleLogin} className="space-y-4">
              {isRegister && (
                <>
                  <div>
                    <Label className="text-gold">Full Name</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your name"
                      className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gold">Phone Number</Label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="09XX XXX XXXX"
                      className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                      required
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label className="text-gold">Email</Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                  required
                />
              </div>
              
              <div>
                <Label className="text-gold">Password</Label>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                  required
                />
              </div>

              {isRegister && (
                <div>
                  <Label className="text-gold">Confirm Password</Label>
                  <Input
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="bg-[#0a0a0a] border-gold/30 text-white mt-1"
                    required
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gold text-black hover:bg-pink hover:text-white font-serif py-6 mt-6"
              >
                {isLoading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-gold hover:text-pink transition-colors"
              >
                {isRegister ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Customer Dashboard
  return (
    <div className="page-transition pt-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-script text-4xl text-gold">My Account</h1>
            <p className="text-[#A3A3A3]">Welcome back, {customer?.name}!</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500 text-red-400 hover:bg-red-500/20"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign Out
          </Button>
        </div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface border border-gold/20 rounded-xl p-6 mb-8"
        >
          <h2 className="font-serif text-xl text-gold-light mb-4">Profile Information</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                <User className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-[#666] text-xs">Name</p>
                <p className="text-white">{customer?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-[#666] text-xs">Email</p>
                <p className="text-white">{customer?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-gold" />
              </div>
              <div>
                <p className="text-[#666] text-xs">Phone</p>
                <p className="text-white">{customer?.phone}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Book New Button */}
        <Link
          to="/contact"
          className="block mb-8"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-r from-gold/20 to-pink/20 border border-gold/30 rounded-xl p-6 flex items-center justify-between hover:border-pink/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-pink flex items-center justify-center">
                <Scissors className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-xl text-gold-light">Book New Appointment</h3>
                <p className="text-[#A3A3A3] text-sm">Schedule your next visit</p>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-gold group-hover:translate-x-1 transition-transform" />
          </motion.div>
        </Link>

        {/* Bookings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-serif text-xl text-gold-light mb-4">My Bookings</h2>
          
          {bookings.length === 0 ? (
            <div className="bg-surface border border-gold/20 rounded-xl p-12 text-center">
              <Calendar className="w-12 h-12 text-gold/30 mx-auto mb-4" />
              <p className="text-[#A3A3A3] mb-4">No bookings yet</p>
              <Link
                to="/contact"
                className="text-gold hover:text-pink transition-colors"
              >
                Book your first appointment →
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => {
                const status = statusColors[booking.status] || statusColors.pending_payment;
                return (
                  <div
                    key={booking.id}
                    className="bg-surface border border-gold/20 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-serif text-lg text-white">
                          {booking.service_label || booking.service}
                        </h3>
                        <p className="text-[#666] text-xs">ID: {booking.id}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gold" />
                        <span className="text-[#A3A3A3]">{booking.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-pink" />
                        <span className="text-[#A3A3A3]">{booking.time}</span>
                      </div>
                    </div>
                    {booking.status === 'pending_payment' && (
                      <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <p className="text-yellow-400 text-sm flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Please complete your ₱500 deposit to confirm this booking
                        </p>
                      </div>
                    )}
                    {booking.status === 'payment_uploaded' && (
                      <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-400 text-sm flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Payment received! Waiting for confirmation...
                        </p>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-green-400 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Your appointment is confirmed! See you soon!
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Account;
